import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notificacaoService } from '@/services/notificacao.service'
import type { NotificacaoTemplate, UUID, PushMessage } from '@/types/database'

const KEYS = {
    templates: ['notificacao_templates'] as const,
    messages: ['push_messages'] as const,
    deliveries: (id: string) => ['push_deliveries', id] as const,
}

export function useTemplates() {
    return useQuery({
        queryKey: KEYS.templates,
        queryFn: () => notificacaoService.listTemplates()
    })
}

export function useCreateTemplate() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: Partial<NotificacaoTemplate>) => notificacaoService.createTemplate(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.templates })
    })
}

export function useUpdateTemplate() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ id, ...data }: Partial<NotificacaoTemplate> & { id: UUID }) =>
            notificacaoService.updateTemplate(id, data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.templates })
    })
}

export function useDeleteTemplate() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: UUID) => notificacaoService.removeTemplate(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.templates })
    })
}

export function usePushMessages() {
    return useQuery({
        queryKey: KEYS.messages,
        queryFn: () => notificacaoService.listMessages()
    })
}

export function useSendNotification() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: Partial<PushMessage>) => notificacaoService.send(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.messages })
        }
    })
}

export function useDeliveries(messageId?: string) {
    return useQuery({
        queryKey: KEYS.deliveries(messageId || 'none'),
        queryFn: () => messageId ? notificacaoService.getDeliveries(messageId) : Promise.resolve([]),
        enabled: !!messageId
    })
}
